// Appealbot B&W design-token UI kit — ported from the Claude Design handoff
// (appeal-generation: components/Button, Icon, Status + src/atoms, ticket-update).
// Minimal, functional, black-and-white, Inter everywhere. Dependency-free:
// icons are inline Lucide-style SVGs so no new npm package / frontend rebuild.
import React from 'react';

// ── Icon (inline Lucide glyphs) ─────────────────────────────────────────────
// Static, trusted path tables rendered as real SVG children (no innerHTML).
const ICON_PARTS = {
  'refresh-cw': [
    ['path', { d: 'M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' }],
    ['path', { d: 'M3 3v5h5' }],
    ['path', { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' }],
    ['path', { d: 'M16 16h5v5' }],
  ],
  'arrow-left': [['path', { d: 'm12 19-7-7 7-7' }], ['path', { d: 'M19 12H5' }]],
  lock: [['rect', { width: 18, height: 11, x: 3, y: 11, rx: 2, ry: 2 }], ['path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }]],
  unlock: [['rect', { width: 18, height: 11, x: 3, y: 11, rx: 2, ry: 2 }], ['path', { d: 'M7 11V7a5 5 0 0 1 9.9-1' }]],
  plus: [['path', { d: 'M5 12h14' }], ['path', { d: 'M12 5v14' }]],
  send: [['path', { d: 'm22 2-7 20-4-9-9-4Z' }], ['path', { d: 'M22 2 11 13' }]],
  'file-text': [
    ['path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }],
    ['path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }],
    ['path', { d: 'M16 13H8' }], ['path', { d: 'M16 17H8' }], ['path', { d: 'M10 9H8' }],
  ],
  download: [['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }], ['path', { d: 'm7 10 5 5 5-5' }], ['path', { d: 'M12 15V3' }]],
  'file-plus': [
    ['path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }],
    ['path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }],
    ['path', { d: 'M9 15h6' }], ['path', { d: 'M12 18v-6' }],
  ],
  x: [['path', { d: 'M18 6 6 18' }], ['path', { d: 'm6 6 12 12' }]],
  check: [['path', { d: 'M20 6 9 17l-5-5' }]],
  'alert-triangle': [
    ['path', { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' }],
    ['path', { d: 'M12 9v4' }], ['path', { d: 'M12 17h.01' }],
  ],
  info: [['circle', { cx: 12, cy: 12, r: 10 }], ['path', { d: 'M12 16v-4' }], ['path', { d: 'M12 8h.01' }]],
  'check-circle': [['circle', { cx: 12, cy: 12, r: 10 }], ['path', { d: 'm9 12 2 2 4-4' }]],
  paperclip: [['path', { d: 'm21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48' }]],
  folder: [['path', { d: 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z' }]],
  'corner-down-right': [['path', { d: 'm15 10 5 5-5 5' }], ['path', { d: 'M4 4v7a4 4 0 0 0 4 4h12' }]],
  'chevron-down': [['path', { d: 'm6 9 6 6 6-6' }]],
  'chevron-up': [['path', { d: 'm18 15-6-6-6 6' }]],
};

export function Icon({ name, size = 16, style = {} }) {
  const parts = ICON_PARTS[name] || [];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style }}
    >
      {parts.map(([tag, props], i) => React.createElement(tag, { key: i, ...props }))}
    </svg>
  );
}

// ── fmtMoney ────────────────────────────────────────────────────────────────
export function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  const s = Math.abs(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (v < 0 ? '-$' : '$') + s;
}

// ── Button ──────────────────────────────────────────────────────────────────
export function Button({ kind = 'secondary', size = 'md', icon, children, style = {}, ...rest }) {
  const base = {
    fontFamily: 'Inter, sans-serif', display: 'inline-flex', alignItems: 'center', gap: 8,
    cursor: rest.disabled ? 'not-allowed' : 'pointer', borderRadius: 4, padding: '10px 14px',
    fontSize: size === 'lg' ? 20 : 14, fontWeight: size === 'lg' ? 700 : 500, lineHeight: 1,
    transition: 'opacity .15s, background .15s', boxSizing: 'border-box',
  };
  const kinds = {
    primary: { background: '#000', color: '#fff', border: '1px solid #000' },
    secondary: { background: '#fff', color: '#000', border: '1px solid #000' },
    ghost: { background: 'transparent', color: '#000', border: 'none' },
    dashed: { background: '#fff', color: '#000', border: '1px dashed #A0ABBB' },
  };
  return (
    <button
      style={{ ...base, ...kinds[kind], ...style }}
      onMouseOver={(e) => { if (!rest.disabled) e.currentTarget.style.opacity = '.8'; }}
      onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
      {...rest}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
}

// ── Chip (status) ───────────────────────────────────────────────────────────
const CHIP_STYLES = {
  denied: { background: '#FD8A8A', color: '#000', border: 'none' },
  approved: { background: '#E6F4EC', color: '#1F8A4C', border: 'none' },
  pending: { background: '#FFFBEB', color: '#B7791F', border: '1px solid #FBBF24' },
  review: { background: '#E8F0FF', color: '#1D4ED8', border: 'none' },
  sent: { background: '#000', color: '#fff', border: 'none' },
  draft: { background: '#F1F3F9', color: '#000', border: 'none' },
};
export function Chip({ status, children }) {
  const st = CHIP_STYLES[(status || '').toLowerCase()] || CHIP_STYLES.draft;
  return (
    <span style={{
      fontFamily: 'Inter', fontSize: 14, fontWeight: 700, padding: '4px 10px', borderRadius: 4,
      display: 'inline-flex', alignItems: 'center', gap: 6, ...st,
    }}>{children ?? status}</span>
  );
}

// ── Banner ──────────────────────────────────────────────────────────────────
export function Banner({ kind = 'info', title, body, action, onAction, onDismiss, children }) {
  const styles = {
    warn: { bg: '#FFFBEB', border: '#FBBF24', icon: 'alert-triangle', iconColor: '#B7791F' },
    info: { bg: '#E8F0FF', border: '#1D4ED8', icon: 'info', iconColor: '#1D4ED8' },
    danger: { bg: '#FEECEB', border: '#FF0004', icon: 'alert-triangle', iconColor: '#FF0004' },
    success: { bg: '#E6F4EC', border: '#1F8A4C', icon: 'check-circle', iconColor: '#1F8A4C' },
  };
  const s = styles[kind] || styles.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, borderLeft: `3px solid ${s.border}`,
      padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start', fontFamily: 'Inter', marginBottom: 20,
    }}>
      <span style={{ color: s.iconColor, display: 'inline-flex', flexShrink: 0, marginTop: 2 }}>
        <Icon name={s.icon} size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontSize: 14, fontWeight: 700, color: '#000', lineHeight: 1.3 }}>{title}</div>}
        {body && <div style={{ fontSize: 13, color: '#1D2433', marginTop: 4, lineHeight: 1.5 }}>{body}</div>}
        {children}
        {action && (
          <div style={{ marginTop: 10 }}>
            <Button kind="primary" size="sm" onClick={onAction} style={{ fontSize: 13, padding: '6px 12px' }}>{action}</Button>
          </div>
        )}
      </div>
      {onDismiss && (
        <span onClick={onDismiss} style={{ cursor: 'pointer', color: '#64748B', display: 'inline-flex' }}>
          <Icon name="x" size={16} />
        </span>
      )}
    </div>
  );
}

// ── PageHeader ──────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, lineHeight: 1, margin: 0, color: '#000' }}>{title}</h1>
        {subtitle && <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B', margin: '8px 0 0', lineHeight: 1.4 }}>{subtitle}</p>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

// ── CardShell ───────────────────────────────────────────────────────────────
export function CardShell({ title, right, children, style = {} }) {
  return (
    <div style={{ border: '1px solid #D1D5DB', background: '#fff', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{title}</div>
        {right}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

export function FactRow({ label, children, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '9px 0', borderBottom: '1px solid #F1F3F9' }}>
      <span style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, monospace' : 'Inter', fontSize: 13, color: '#000', fontWeight: 600, textAlign: 'right' }}>{children}</span>
    </div>
  );
}

export function StripStat({ label, value }) {
  return (
    <div style={{ minWidth: 'max-content' }}>
      <div style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#000', marginTop: 3, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

// ── Activity timeline ───────────────────────────────────────────────────────
export function Timeline({ events, pending }) {
  const nodeColor = (e) =>
    e.kind === 'outcome' ? (e.tone === 'approved' ? '#1F8A4C' : '#FF0004') : e.role === 'payer' ? '#1D4ED8' : '#000';
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: 7, top: 6, bottom: pending ? 38 : 6, width: 1, background: '#E5E7EB' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {events.map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 14, position: 'relative' }}>
            <div style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, marginTop: 2, background: nodeColor(e), border: `2px solid ${nodeColor(e)}`, boxShadow: '0 0 0 4px #fff' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: '#000' }}>{e.title}</span>
                {e.role === 'payer' && (
                  <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, color: '#1D4ED8', background: '#E8F0FF', padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Payer</span>
                )}
              </div>
              {e.date && <div style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', marginTop: 2 }}>{e.date}{e.actor ? ` · ${e.actor}` : ''}</div>}
              {e.body && (
                <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#1D2433', lineHeight: 1.5, marginTop: 8, background: e.role === 'payer' ? '#F8F9FC' : '#fff', border: e.role === 'payer' ? '1px solid #E5E7EB' : 'none', padding: e.role === 'payer' ? '10px 12px' : 0 }}>{e.body}</div>
              )}
              {e.meta && <div style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', marginTop: 6 }}>{e.meta}</div>}
            </div>
          </div>
        ))}
        {pending && (
          <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
            <div style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, marginTop: 2, background: '#fff', border: '2px dashed #94A3B8', boxShadow: '0 0 0 4px #fff' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: '#1D2433' }}>{pending.title}</div>
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B', marginTop: 4, lineHeight: 1.5 }}>{pending.body}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Disputed lines ──────────────────────────────────────────────────────────
export function DisputedLines({ lines }) {
  const total = lines.reduce((s, l) => s + (Number(l.line_amount_owed) || 0), 0);
  const maskKey = (k) => (k && k.length > 4 ? '••••' + k.slice(-4) : k || '—');
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, fontFamily: 'Inter', fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, paddingBottom: 8, borderBottom: '1px solid #E5E7EB' }}>
        <span>lineKey · CPT</span><span style={{ textAlign: 'right' }}>Owed</span>
      </div>
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F3F9' }}>
          <div>
            <code style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 12, color: '#000', fontWeight: 700 }}>{maskKey(l.optum_line_key)}</code>
            <span style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B', marginLeft: 8 }}>{l.procedure_code || '—'}</span>
          </div>
          <span style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.line_amount_owed)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 12 }}>
        <span style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>claimAmountOwed</span>
        <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(total)}</span>
      </div>
    </div>
  );
}

// ── Attachments list ────────────────────────────────────────────────────────
// Maps a per-doc upload_status to a Chip status + icon + label. Reuses the
// shared Chip palette (CHIP_STYLES) — no new tokens / raw hex.
const UPLOAD_STATUS_PILL = {
  uploaded: { chip: 'approved', icon: 'check', label: 'Uploaded' },
  failed: { chip: 'denied', icon: 'x', label: 'Failed' },
  pending: { chip: 'draft', icon: 'clock', label: 'Pending' },
};

export function AttachmentList({ attachments }) {
  if (!attachments.length) {
    return <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B' }}>No attachments on this ticket.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {attachments.map((a, i) => {
        const status = (a.upload_status || 'pending').toLowerCase();
        const pill = UPLOAD_STATUS_PILL[status] || UPLOAD_STATUS_PILL.pending;
        const failed = status === 'failed';
        return (
          <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < attachments.length - 1 ? '1px solid #F1F3F9' : 'none' }}>
            <Icon name="file-text" size={16} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.filename || a.original_filename || 'Document'}</div>
              {failed && a.last_error_code && (
                <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 11, color: '#64748B', marginTop: 2 }}>
                  {a.last_error_code}
                </div>
              )}
            </div>
            <Chip status={pill.chip}>
              <Icon name={pill.icon} size={12} /> {pill.label}
            </Chip>
            <code style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 11, fontWeight: 700, color: '#000', background: '#fff', border: '1px solid #D1D5DB', padding: '1px 6px' }}>{a.document_type_code || 'M1'}</code>
          </div>
        );
      })}
    </div>
  );
}

// ── Form atoms ──────────────────────────────────────────────────────────────
export function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      {label && <div style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: '#000', marginBottom: 6 }}>{label}</div>}
      {children}
      {hint && <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B', marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

export function Select({ value, options, onChange }) {
  // Options may be plain strings or { value, label } objects (e.g. reason codes):
  // render the label, submit the value.
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', fontFamily: 'Inter', fontSize: 14, padding: '10px 12px', border: '1px solid #000', background: '#fff', cursor: 'pointer' }}>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Textarea({ rows = 4, value, placeholder, onChange }) {
  return (
    <textarea rows={rows} value={value} placeholder={placeholder} onChange={onChange} style={{ width: '100%', fontFamily: 'Inter', fontSize: 14, padding: '10px 12px', border: '1px solid #000', background: '#fff', resize: 'vertical', boxSizing: 'border-box' }} />
  );
}

export function SectionHeader({ step, title, subtitle, complete }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: complete ? '#000' : '#fff', border: '1px solid #000', color: complete ? '#fff' : '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
        {complete ? <Icon name="check" size={16} /> : step}
      </div>
      <div style={{ flex: 1, paddingTop: 4 }}>
        <h2 style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, margin: 0, lineHeight: 1.1 }}>{title}</h2>
        {subtitle && <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B', margin: '4px 0 0', lineHeight: 1.4 }}>{subtitle}</p>}
      </div>
    </div>
  );
}

export function SectionBody({ children, style = {} }) {
  return (
    <div style={{ paddingLeft: 48, paddingBottom: 32, borderLeft: '1px solid #E5E7EB', marginLeft: 15, marginTop: -12, paddingTop: 12, ...style }}>
      {children}
    </div>
  );
}

export function Modal({ open, title, children, footer, onClose }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: '1px solid #000', width: 'min(560px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #000' }}>
          <div style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700 }}>{title}</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#64748B', display: 'inline-flex' }}><Icon name="x" size={18} /></span>
        </div>
        <div style={{ padding: 16, overflow: 'auto' }}>{children}</div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid #E5E7EB' }}>{footer}</div>}
      </div>
    </div>
  );
}

// Static fallback only. The per-payer set is now LIVE-captured by the backend
// from getClaimLineAction (.allowedReconReasons) and served via the ticket-detail
// `reason_choices`; deriveUpdateChoices prefers that, so this list is used only
// before the first status refresh has captured the payer's reasons.
export const ALLOWED_RECON_REASONS = [
  'Medical Records Provided', 'Coordination of Benefits', 'Notification, Authorization or Referral',
  'Paper or Electronic Check', 'Patient Eligibility', 'Policy', 'Reimbursement Rate', 'Timely Filing',
  'Corrected Claim Submission', 'Submit requested OrthoNet medical records', 'Out of Network Negotiation',
  'Amended Contract – SPI or Medical Necessity', 'WA Medicaid Resubmission', 'Overpayment and Recoupment Request',
];

export const DOC_TYPE_CODES = [
  { code: 'M1', desc: 'Medical records' },
  { code: 'OB', desc: 'Explanation of benefits' },
  { code: 'CT', desc: 'Certification / authorization' },
  { code: 'DA', desc: 'Diagnostic report' },
  { code: '04', desc: 'Other clinical documentation' },
];

// Derive the reason / doc-type option lists for the Update view, preferring the
// backend/Optum-curated choices from the ticket-detail response and falling back
// to the static lists above only when the API did not provide them (finding #3).
export function deriveUpdateChoices(ticket) {
  // Reason choices stay { value, label }: render the label, SUBMIT the value (code).
  // Optum / the serializer validate the reason CODE (e.g. "INCORRECT_PAYMENT"), not
  // the display label. Static fallback labels carry no separate code, so value=label.
  const apiReasons = (ticket.reason_choices || [])
    .filter((c) => c && c.value)
    .map((c) => ({ value: c.value, label: c.label || c.value }));
  const reasonChoices = apiReasons.length
    ? apiReasons
    : ALLOWED_RECON_REASONS.map((label) => ({ value: label, label }));
  const apiDocTypes = (ticket.document_type_choices || [])
    .map((c) => ({ code: c.value, desc: c.label }))
    .filter((c) => c.code);
  const docTypeChoices = apiDocTypes.length ? apiDocTypes : DOC_TYPE_CODES;
  return { reasonChoices, docTypeChoices };
}

export function canSubmitTicketUpdate(ticket) {
  if (!ticket || ticket.is_closed || !ticket.ticket_number) return false;
  const allowed = (ticket.allowed_action || '').trim().toLowerCase();
  return allowed === 'update' || allowed === 'view';
}

// Primary-CTA decision for the ticket detail page, ported from the Claude Design
// ticket-update screen. Returns 'update' | 'start-new' | 'resolved' | 'unavailable':
//   open + ticketNumber + allowedAction=Update/View → Create Update
//   closed + Upheld/denied      → Start New Reconsideration (allowedAction back to Create)
//   closed + Overturned/approved→ Resolved — Overturned (won; nothing to do)
//   otherwise                   → Update Unavailable
export function updateCtaMode(ticket, canUpdate) {
  if (canUpdate) return 'update';
  const outcome = (ticket.ticket_outcome || '').toLowerCase();
  if (ticket.is_closed && (outcome.includes('uphel') || outcome.includes('den'))) return 'start-new';
  if (ticket.is_closed && (outcome.includes('overturn') || outcome.includes('approv'))) return 'resolved';
  return 'unavailable';
}
